/**
 * 概览页：后台第一屏。
 *
 * 所有数字来自 `GET /api/admin/overview`，并按登录员工的可见范围聚合 ——
 * 界面不写死任何业务数字，也不做「看起来正常」的兜底：
 * 短信通道是 console 时必须显式提示「未接入真实通道」。
 */
import { adminApi } from '../lib/api.ts';
import type { AlertItem } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, Kpi, PageHead } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  alertLevelFull,
  alertLevelTone,
  alertStatusLabel,
  formatDate,
  scopeLabel,
  smsChannelNotice,
} from '../lib/format.ts';

const ALERT_LEVELS = ['danger', 'warn', 'info'] as const;

/**
 * 预警计数的读取：**字段缺失时显示「未下发」，绝不显示 0** ——
 * 把「看不到」显示成「0 条未解除」会让值班人员以为一切正常。
 */
function alertCountText(source: unknown, level: string): string {
  if (typeof source !== 'object' || source === null) return '未下发';
  const value = (source as Record<string, unknown>)[level];
  return typeof value === 'number' ? String(value) : '未下发';
}

export function OverviewPage() {
  const { data, error, loading, reload } = useLoad((signal) => adminApi.overview(signal));
  const recent = useLoad((signal) => adminApi.alerts('', 5, signal));

  if (loading) return <LoadingBox text="正在加载概览…" />;
  if (error) return <ErrorBox message={errorText(error)} onRetry={reload} />;
  if (!data) return <ErrorBox message="概览数据为空" onRetry={reload} />;

  const sms = smsChannelNotice(data.channels.sms);
  const alerts: Record<string, number> = data.alerts;
  const openAlerts = (alerts.danger ?? 0) + (alerts.warn ?? 0) + (alerts.info ?? 0);

  const recentItems: AlertItem[] = recent.data?.items ?? [];

  return (
    <div>
      <PageHead
        title="控制台概览"
        sub="数据按当前登录员工的可见范围聚合；范围外成员不计入任何一项"
      />

      <div className="grid4">
        <Kpi
          label="在册活跃成员"
          value={data.members.active}
          delta={`可见范围：${scopeLabel(data.members.scope)}`}
        />
        <Kpi
          label="三级未解除预警"
          value={alertCountText(alerts, 'danger')}
          tone="danger"
          delta="最高优先级，处置时限最短"
        />
        <Kpi
          label="二级未解除预警"
          value={alertCountText(alerts, 'warn')}
          tone="warn"
          delta="需人工确认后派单"
        />
        <Kpi label="一级未解除预警" value={alertCountText(alerts, 'info')} delta="观察项，纳入随访" />
      </div>

      <div className="sp12" />

      <div className="grid2">
        <Card title="预警分级" sub="未解除">
          <table className="tbl">
            <thead>
              <tr>
                <th>等级</th>
                <th>说明</th>
                <th style={{ textAlign: 'right' }}>未解除</th>
              </tr>
            </thead>
            <tbody>
              {ALERT_LEVELS.map((level) => (
                <tr key={level}>
                  <td>
                    <span className={`badge ${alertLevelTone(level)}`}>{alertLevelFull(level)}</span>
                  </td>
                  <td>
                    {level === 'danger'
                      ? '已确认的急性风险，需立即介入'
                      : level === 'warn'
                        ? '指标异常波动，需人工确认'
                        : '趋势提示，纳入随访观察'}
                  </td>
                  <td className="num-cell">{alertCountText(alerts, level)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="note-src">
            <b>口径</b>
            <br />
            未解除 = <span className="mono-num">status != 'resolved'</span>；合计 {openAlerts} 条
            （三级 {alertCountText(alerts, 'danger')} · 二级 {alertCountText(alerts, 'warn')} · 一级{' '}
            {alertCountText(alerts, 'info')}）。计数范围与当前可见范围一致。
          </div>
        </Card>

        <div>
          <Card title="报告解析失败" sub="需人工介入">
            <div className="row">
              <div className="kpi grow">
                <div className="k-l">解析失败报告数</div>
                <div className="k-v danger">{data.reports.parseFailed}</div>
                <div className="k-d">解析失败的报告不会被计入指标，避免用残缺数据做判断</div>
              </div>
            </div>
            <div className="note-src">
              <b>为什么单列一项</b>
              <br />
              报告解析失败会让该成员的健康档案缺少增量数据，属于必须人工处理的运营事件，
              因此放在概览页而不是埋在报告列表里。
            </div>
          </Card>

          <Card title="通道状态" sub="如实上报">
            <div className={`alert-chip ${sms.warn ? 'warn' : 'ok'}`}>
              <span>{sms.warn ? '⚠' : '✓'}</span>
              <span>
                <b>短信通道：{sms.label}</b>
                <br />
                {sms.detail}
              </span>
            </div>
            <div className="note-src">
              <b>短信通道 🔒 说明</b>
              <br />
              通道为 <span className="mono-num">console</span> 时，验证码只写服务日志：
              成员端的登录短信与强触达预警<b>都没有真正发出</b>。后台必须如实展示这一状态，
              否则会出现「页面显示一切正常、实际一条短信都没发出去」。
            </div>
          </Card>
        </div>
      </div>

      <Card title="最新未解除预警" sub="按等级与发现时间排序" tight>
        {recent.loading && <LoadingBox text="正在加载预警…" />}
        {recent.error !== null && <ErrorBox message={errorText(recent.error)} onRetry={recent.reload} />}
        {recent.data && recentItems.length === 0 && (
          <div className="state-box">
            <div className="st-ico">◍</div>
            <div className="st-title">当前可见范围内没有未解除预警</div>
          </div>
        )}
        {recentItems.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>等级</th>
                  <th>预警标题</th>
                  <th>成员</th>
                  <th>状态</th>
                  <th>发现时间</th>
                </tr>
              </thead>
              <tbody>
                {recentItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className={`badge ${alertLevelTone(item.level)}`}>{alertLevelFull(item.level)}</span>
                    </td>
                    <td>{item.title}</td>
                    <td>
                      {item.member.name}
                      <span className="muted mono-num"> · {item.member.no}</span>
                    </td>
                    <td>{alertStatusLabel(item.status)}</td>
                    <td className="mono-num">{formatDate(item.foundAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
